import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    FaBan,
    FaBuilding,
    FaCalendarAlt,
    FaCheck,
    FaDownload,
    FaEnvelope,
    FaEye,
    FaFilter,
    FaGlobe,
    FaIndustry,
    FaMapMarkerAlt,
    FaPhone,
    FaSearch,
    FaTimes,
    FaTrash,
    FaUserCheck,
    FaUserTimes,
    FaUsers
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const Employers = () => {
  const [employers, setEmployers] = useState([]);
  const [filteredEmployers, setFilteredEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApproval, setFilterApproval] = useState('all');
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [employersPerPage] = useState(10);
  const { apiCall } = useAuth();

  // Pagination calculations
  const indexOfLastEmployer = currentPage * employersPerPage;
  const indexOfFirstEmployer = indexOfLastEmployer - employersPerPage;
  const currentEmployers = filteredEmployers.slice(indexOfFirstEmployer, indexOfLastEmployer);
  const totalPages = Math.ceil(filteredEmployers.length / employersPerPage);

  useEffect(() => {
    fetchEmployers();
  }, []);

  useEffect(() => {
    filterEmployers();
  }, [employers, searchTerm, filterStatus, filterApproval]);

  const fetchEmployers = async () => {
    try {
    setLoading(true);
      console.log('🔍 FETCHING EMPLOYERS - Request started');
      
      // Explicitly request all employers to ensure we get both pending and approved
      const response = await apiCall('GET', '/api/employers?status=all');
      console.log('📊 EMPLOYERS API RESPONSE:', {
        success: response.success,
        dataStructure: Object.keys(response.data || {}),
        responseData: response.data
      });
      
      let employerArray = [];
      if (response.data?.employers) {
        employerArray = response.data.employers;
      } else if (response.data?.data) {
        employerArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        employerArray = response.data;
      } else if (response.employers) {
        employerArray = response.employers;
      }

      console.log('✅ PROCESSED EMPLOYERS:', {
        totalCount: employerArray.length,
        approvedCount: employerArray.filter(emp => emp.isApproved === true).length,
        pendingCount: employerArray.filter(emp => emp.isApproved === false).length,
        activeCount: employerArray.filter(emp => emp.isActive === true).length,
        inactiveCount: employerArray.filter(emp => emp.isActive === false).length,
        employers: employerArray.map(emp => ({
          id: emp._id,
          companyName: emp.companyName,
          email: emp.email,
          isApproved: emp.isApproved,
          isActive: emp.isActive
        }))
      });

      setEmployers(employerArray);
    } catch (error) {
      console.error('❌ FAILED TO FETCH EMPLOYERS:', error);
      toast.error('Failed to fetch employers');
    } finally {
      setLoading(false);
    }
  };

  const filterEmployers = () => {
    console.log('🔍 FILTERING EMPLOYERS:', {
      totalEmployers: employers.length,
      searchTerm,
      filterStatus,
      filterApproval,
      employersData: employers.map(emp => ({
        id: emp._id,
        companyName: emp.companyName,
        isApproved: emp.isApproved,
        isActive: emp.isActive
      }))
    });

    let filtered = employers;

    if (searchTerm) {
      const beforeSearch = filtered.length;
      filtered = filtered.filter(employer => {
        const companyName = employer.companyName?.toLowerCase() || '';
        const email = employer.email?.toLowerCase() || '';
        const industry = employer.industry?.toLowerCase() || '';
        const searchLower = searchTerm.toLowerCase();
        
        return companyName.includes(searchLower) || 
               email.includes(searchLower) || 
               industry.includes(searchLower);
      });
      console.log(`🔍 SEARCH FILTER: ${beforeSearch} → ${filtered.length} employers`);
    }

    if (filterStatus !== 'all') {
      const beforeStatus = filtered.length;
      filtered = filtered.filter(employer => {
        switch (filterStatus) {
          case 'active':
            return employer.isActive === true;
          case 'inactive':
            return employer.isActive === false;
          default:
            return true;
        }
      });
      console.log(`🔍 STATUS FILTER (${filterStatus}): ${beforeStatus} → ${filtered.length} employers`);
    }

    if (filterApproval !== 'all') {
      const beforeApproval = filtered.length;
      filtered = filtered.filter(employer => {
        switch (filterApproval) {
          case 'approved':
            return employer.isApproved === true;
          case 'pending':
            return employer.isApproved === false;
          default:
            return true;
        }
      });
      console.log(`🔍 APPROVAL FILTER (${filterApproval}): ${beforeApproval} → ${filtered.length} employers`);
    }

    console.log('✅ FINAL FILTERED EMPLOYERS:', {
      count: filtered.length,
      employers: filtered.map(emp => ({
        id: emp._id,
        companyName: emp.companyName,
        isApproved: emp.isApproved,
        isActive: emp.isActive
      }))
    });

    setFilteredEmployers(filtered);
    setCurrentPage(1);
  };

  const handleApprovalChange = async (employerId, approved) => {
    try {
      console.log('🔄 APPROVAL CHANGE REQUEST:', {
        employerId,
        approved,
        timestamp: new Date().toISOString()
      });

      // Prevent multiple clicks by disabling the button
      const approveButton = document.querySelector(`[data-testid="approve-employer-${employerId}"]`);
      if (approveButton) {
        approveButton.disabled = true;
        approveButton.style.opacity = '0.5';
        approveButton.style.cursor = 'not-allowed';
      }

      const endpoint = approved ? `/api/employers/${employerId}/approve` : `/api/employers/${employerId}/decline`;
      console.log('📡 API CALL:', { method: 'PATCH', endpoint });
      
      const response = await apiCall('PATCH', endpoint);
      
      console.log('📊 APPROVAL RESPONSE:', {
        success: response.success,
        message: response.data?.message || response.message,
        data: response.data,
        error: response.error
      });
      
      if (response.success) {
        console.log('✅ API CALL SUCCESSFUL - Showing success toast');
        toast.success(`Employer ${approved ? 'approved' : 'declined'} successfully`);
        
        console.log('🔄 REFETCHING EMPLOYERS AFTER APPROVAL...');
        await fetchEmployers();
        
        console.log('✅ APPROVAL PROCESS COMPLETED');
      } else {
        console.error('❌ API CALL FAILED:', response);
        toast.error(response.error || 'Failed to update approval status');
        
        // Re-enable button on failure
        if (approveButton) {
          approveButton.disabled = false;
          approveButton.style.opacity = '1';
          approveButton.style.cursor = 'pointer';
        }
      }
    } catch (error) {
      console.error('❌ APPROVAL ERROR CAUGHT:', error);
      toast.error('Failed to update approval status - Network or server error');
      
      // Re-enable button on error
      const approveButton = document.querySelector(`[data-testid="approve-employer-${employerId}"]`);
      if (approveButton) {
        approveButton.disabled = false;
        approveButton.style.opacity = '1';
        approveButton.style.cursor = 'pointer';
      }
    }
  };

  const handleStatusChange = async (employerId, isActive) => {
    try {
      const response = await apiCall('PATCH', `/api/employers/${employerId}/status`, { isActive });
      
      if (response.success) {
        toast.success(`Employer ${isActive ? 'activated' : 'deactivated'} successfully`);
      fetchEmployers();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteEmployer = async (employerId) => {
    if (window.confirm('Are you sure you want to delete this employer? This action cannot be undone.')) {
      try {
        const response = await apiCall('DELETE', `/api/employers/${employerId}`);
        
        if (response.success) {
          toast.success('Employer deleted successfully');
      fetchEmployers();
        }
      } catch (error) {
        console.error('Failed to delete employer:', error);
        toast.error('Failed to delete employer');
      }
    }
  };

  const openModal = (employer) => {
    setSelectedEmployer(employer);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEmployer(null);
  };

  const getStatusBadge = (employer) => {
    if (!employer.isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <FaFilter className="mr-1" />
          Pending Approval
        </span>
      );
    }
    
    if (employer.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaUserCheck className="mr-1" />
          Active
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <FaBan className="mr-1" />
        Inactive
      </span>
    );
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employers Management</h1>
          <p className="text-gray-600 mt-1">Manage employer accounts, approvals, and permissions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-colors">
            <FaDownload className="mr-2" />
            Export Data
          </button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Employers</p>
              <p className="text-2xl font-bold text-gray-900">{employers.length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <FaBuilding className="text-white text-xl" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {employers.filter(e => e.isActive && e.isApproved).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-500 rounded-xl flex items-center justify-center">
              <FaUserCheck className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-600">
                {employers.filter(e => !e.isApproved).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-yellow-500 rounded-xl flex items-center justify-center">
              <FaFilter className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-red-600">
                {employers.filter(e => !e.isActive).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-red-500 rounded-xl flex items-center justify-center">
              <FaUserTimes className="text-white text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by company name, email, or industry..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="lg:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="lg:w-48">
            <select
              value={filterApproval}
              onChange={(e) => setFilterApproval(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Approvals</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Employers Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
      >
        {currentEmployers.length === 0 ? (
          <div className="text-center py-12">
            <FaBuilding className="mx-auto text-4xl text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No employers found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Company</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Contact</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Industry</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Size</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Joined</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentEmployers.map((employer) => (
                    <tr key={employer._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">
                              {employer.companyName?.charAt(0).toUpperCase() || 'C'}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{employer.companyName || 'Unknown Company'}</p>
                            <p className="text-sm text-gray-500">{employer.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <FaEnvelope className="mr-2 text-gray-400" />
                            <span className="truncate max-w-[200px]">{employer.email}</span>
                          </div>
                          {employer.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <FaPhone className="mr-2 text-gray-400" />
                              {employer.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaIndustry className="mr-2 text-gray-400" />
                          {employer.industry || 'Not specified'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaUsers className="mr-2 text-gray-400" />
                          {employer.size || 'Not specified'}
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(employer)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaCalendarAlt className="mr-2 text-gray-400" />
                          {new Date(employer.createdAt).toLocaleDateString()}
                        </div>
                  </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => openModal(employer)}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          
                          {!employer.isApproved && (
                            <button
                              onClick={() => handleApprovalChange(employer._id, true)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              title="Approve Employer"
                              data-testid={`approve-employer-${employer._id}`}
                              data-employer-id={employer._id}
                              data-action="approve"
                            >
                              <FaCheck />
                            </button>
                          )}
                          
                          {employer.isActive ? (
                            <button
                              onClick={() => handleStatusChange(employer._id, false)}
                              className="p-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                              title="Deactivate Employer"
                            >
                              <FaBan />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(employer._id, true)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              title="Activate Employer"
                            >
                              <FaUserCheck />
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDeleteEmployer(employer._id)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="Delete Employer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstEmployer + 1} to {Math.min(indexOfLastEmployer, filteredEmployers.length)} of {filteredEmployers.length} employers
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-lg ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* View Modal */}
      {showModal && selectedEmployer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <FaBuilding className="mr-3 text-blue-500" />
                  {selectedEmployer.companyName}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-6">
                {/* Company Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <FaBuilding className="mr-3 text-blue-500" />
                    Company Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company Name</label>
                        <p className="text-gray-900 font-medium">{selectedEmployer.companyName}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email Address</label>
                        <p className="text-gray-900 font-medium">{selectedEmployer.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone Number</label>
                        <p className="text-gray-900 font-medium">{selectedEmployer.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Industry</label>
                        <p className="text-gray-900 font-medium">{selectedEmployer.industry || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company Size</label>
                        <p className="text-gray-900 font-medium">{selectedEmployer.size || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Website</label>
                        {selectedEmployer.website ? (
                          <a 
                            href={selectedEmployer.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
                          >
                            <FaGlobe className="mr-2" />
                            {selectedEmployer.website}
                          </a>
                        ) : (
                          <p className="text-gray-900 font-medium">Not provided</p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Registration Date</label>
                        <p className="text-gray-900 font-medium">
                          {new Date(selectedEmployer.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {selectedEmployer.description && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-500">Company Description</label>
                      <p className="text-gray-900 font-medium mt-1 p-3 bg-gray-50 rounded-lg">
                        {selectedEmployer.description}
                      </p>
                    </div>
                  )}
                </div>

                {/* Company Address */}
                {selectedEmployer.address && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <FaMapMarkerAlt className="mr-3 text-red-500" />
                      Company Address
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Street Address</label>
                          <p className="text-gray-900 font-medium">{selectedEmployer.address || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">City</label>
                          <p className="text-gray-900 font-medium">{selectedEmployer.city || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">State/Province</label>
                          <p className="text-gray-900 font-medium">{selectedEmployer.state || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Country</label>
                          <p className="text-gray-900 font-medium">{selectedEmployer.country || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Status */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <FaUserCheck className="mr-3 text-orange-500" />
                    Account Status
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Approval Status</label>
                      <p className="text-gray-900 font-medium">
                        {selectedEmployer.isApproved ? (
                          <span className="text-green-600 flex items-center">
                            <FaCheck className="mr-1" /> Approved
                          </span>
                        ) : (
                          <span className="text-yellow-600 flex items-center">
                            <FaFilter className="mr-1" /> Pending Approval
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Account Status</label>
                      <p className="text-gray-900 font-medium">
                        {selectedEmployer.isActive ? (
                          <span className="text-green-600 flex items-center">
                            <FaUserCheck className="mr-1" /> Active
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center">
                            <FaBan className="mr-1" /> Inactive
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Updated</label>
                      <p className="text-gray-900 font-medium">
                        {new Date(selectedEmployer.updatedAt || selectedEmployer.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
            </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              {!selectedEmployer.isApproved && (
                    <button
                      onClick={() => {
                        handleApprovalChange(selectedEmployer._id, true);
                        closeModal();
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium flex items-center transition-colors"
                    >
                      <FaCheck className="mr-2" />
                      Approve Employer
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      handleStatusChange(selectedEmployer._id, !selectedEmployer.isActive);
                      closeModal();
                    }}
                    className={`px-6 py-2 rounded-lg font-medium flex items-center transition-colors ${
                      selectedEmployer.isActive 
                        ? "bg-yellow-600 hover:bg-yellow-700 text-white" 
                        : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                  >
                    {selectedEmployer.isActive ? (
                      <>
                        <FaBan className="mr-2" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <FaUserCheck className="mr-2" />
                        Activate
                      </>
                    )}
                  </button>

                  <button
                    onClick={closeModal}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employers; 